// Host contract for Symphony v2 inside the authenticated Speak app.
// Symphony registers matching server tools; the page executes UI actions.

export const SYMPHONY_CHANNEL = "speak.symphony.v1";
export const SPEAK_HOST = "app.speak.ad";
export const DEFAULT_SYMPHONY_LOADER = "https://symphony-vidara.vercel.app/v2/symphony.js";
export const DEFAULT_SYMPHONY_GATEWAY = "https://symphony-vidara.vercel.app";

export const SYMPHONY_ALLOWED_ORIGINS = [
  "https://symphony-vidara.vercel.app",
  "https://edge.symphony.ac",
] as const;

export interface SymphonySessionContext {
  host: typeof SPEAK_HOST;
  supabaseAccessToken: string;
  expiresAt: number | null;
  userId: string;
}

export interface SymphonyIdentify {
  id: string;
}

export function symphonySessionContext(session: {
  access_token: string;
  expires_at?: number | null;
  user: { id: string };
}): SymphonySessionContext {
  return {
    host: SPEAK_HOST,
    supabaseAccessToken: session.access_token,
    expiresAt: session.expires_at ?? null,
    userId: session.user.id,
  };
}

export const AGENT_INDUSTRIES = [
  "Legal", "Healthcare", "Real Estate", "Finance", "Insurance",
  "Technology", "Education", "Retail", "Hospitality", "Manufacturing",
] as const;

export const AGENT_USE_CASES = [
  "Lead Generation", "Customer Service", "Follow-up Calls", "Survey Collection",
  "Debt Collection", "Support Tickets", "Appointment Booking", "Sales Outreach",
] as const;

export const AGENT_LANGUAGES = ["en", "es", "fr", "de", "it", "pt", "nl", "ja", "zh", "ko"] as const;
export const AGENT_GRADES = ["basic", "tools", "tools_kb"] as const;
export const DEMO_EMBED_TYPES = ["elevenlabs", "symphony", "livekit", "anam", "telnyx", "grok"] as const;
export const NUMBER_TYPES = ["local", "toll_free", "mobile"] as const;

export interface AgentCreatePrefill {
  name?: string;
  description?: string;
  industry?: (typeof AGENT_INDUSTRIES)[number];
  useCase?: (typeof AGENT_USE_CASES)[number];
  systemPrompt?: string;
  firstMessage?: string;
  language?: (typeof AGENT_LANGUAGES)[number];
  grade?: (typeof AGENT_GRADES)[number];
}

export interface DemoCreatePrefill {
  customerName?: string;
  slug?: string;
  embedType?: (typeof DEMO_EMBED_TYPES)[number];
  contactEmail?: string;
  contactPhone?: string;
  demoPhoneDisplay?: string;
  widgetSnippet?: string;
}

export interface NumberAttachPrefill {
  countryCode?: string;
  numberType?: (typeof NUMBER_TYPES)[number];
}

export interface SymphonyRequestMessage {
  source: "symphony";
  channel: typeof SYMPHONY_CHANNEL;
  requestId: string;
  type: "action" | "account.context";
  name?: string;
  args?: unknown;
}

export interface SpeakResultMessage {
  source: "speak";
  channel: typeof SYMPHONY_CHANNEL;
  type: "result";
  requestId: string;
  ok: boolean;
  submitted: false;
  result?: unknown;
  error?: string;
}

const EXACT_PATHS = new Set([
  "/dashboard",
  "/campaigns",
  "/campaigns/new",
  "/leads",
  "/agents",
  "/get-started",
  "/voice-agents",
  "/call-logs",
  "/calendar",
  "/settings",
  "/profile",
  "/billing",
  "/analytics",
  "/compliance",
  "/brand-intelligence",
  "/admin",
  "/api-docs",
  "/demo-widget",
]);

function isId(segment: string): boolean {
  return /^[A-Za-z0-9_-]{1,80}$/.test(segment);
}

export function isAllowedAppPathname(pathname: string): boolean {
  if (EXACT_PATHS.has(pathname)) return true;
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 2 && parts[0] === "campaigns" && isId(parts[1])) return true;
  if (parts.length === 3 && parts[0] === "campaigns" && isId(parts[1]) && parts[2] === "edit") return true;
  if (parts.length === 2 && parts[0] === "leads" && isId(parts[1])) return true;
  if (parts.length === 2 && parts[0] === "brand-intelligence" && isId(parts[1])) return true;
  if (parts.length === 2 && parts[0] === "demo" && isId(parts[1])) return true;
  return false;
}

/** In-app path only. Rejects external URLs, protocol-relative paths, and unknown routes. */
export function resolveAppPath(input: string): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.includes("\\") || trimmed.includes("://")) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(trimmed, "https://app.speak.ad");
  } catch {
    return null;
  }
  if (url.origin !== "https://app.speak.ad") return null;
  if (!isAllowedAppPathname(url.pathname)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function isAllowedSymphonyMessageOrigin(origin: string, pageOrigin: string, gateway: string): boolean {
  if (origin === pageOrigin) return true;
  if ((SYMPHONY_ALLOWED_ORIGINS as readonly string[]).includes(origin)) return true;
  try {
    return origin === new URL(gateway).origin;
  } catch {
    return false;
  }
}
